import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '@utils'
import { ChevronUp, HelpCircle } from 'lucide-react'
import { useSessionStore } from '@stores'

interface OrderTotalsInfoProps {
  order: {
    id: number
    currency: string
    subtotal: string
    shipping_price: string
    hidden_fees: string
    discount: string
    tax: string
    tip: string
    total_discounted_price: string
    past_balance?: string
    forward_balance?: string
    amount_paid?: string
    uncaptured_auth?: string
    store_debit?: string
    amount_voided?: string
    settled_refund_amount?: string
    pending_refund_amount?: string
    store_credit?: string
    chargeback_amount?: string
    total_remaining_balance?: string
    amount_due?: string
    [key: string]: any
  }
}

const formatCurrency = (amount: string | number | undefined, currency: string): string => {
  const num = parseFloat(String(amount ?? '0'))
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(num)
}

const isPositive = (amount: string | number | undefined): boolean => parseFloat(String(amount ?? '0')) > Number.EPSILON

interface RowProps {
  label: React.ReactNode
  value: string
  cancelled?: boolean
  pending?: boolean
  bold?: boolean
}

const Row = ({ label, value, cancelled, pending, bold }: RowProps) => (
  <div className={cn('totals-row', bold && 'is-strong')}>
    <span className="totals-label">{label}</span>
    <span className={cn('totals-value notranslate', cancelled && 'is-cancelled', pending && 'is-pending')}>
      {value}
    </span>
  </div>
)

const OrderTotalsInfo = ({ order }: OrderTotalsInfoProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const session = useSessionStore(s => s.session)

  if (session?.show_order_summary !== true) {
    return null
  }

  const showHiddenFees = isPositive(order.hidden_fees)
  const showShipping = isPositive(order.shipping_price) || !showHiddenFees
  const currency = order.currency

  return (
    <div className={cn('order-totals', isExpanded && 'is-expanded')}>
      <div className="totals-toggle-row">
        <button type="button" className="totals-toggle-btn" onClick={() => setIsExpanded(v => !v)}>
          <span>{isExpanded ? 'Hide' : 'Show'}</span> Billing Summary
          <ChevronUp className={cn('transition-transform', !isExpanded && 'rotate-180')} size={14} />
        </button>
        {!isExpanded && (
          <div className="totals-collapsed-amount">{formatCurrency(order.total_discounted_price, currency)}</div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="totals-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] as const }}
            style={{ overflow: 'hidden' }}>
            <div className="totals-body">
              <div className="totals-group">
                <Row label="Subtotal:" value={formatCurrency(order.subtotal, currency)} />
                {showShipping && <Row label="Shipping:" value={formatCurrency(order.shipping_price, currency)} />}
                {showHiddenFees && (
                  <Row
                    label={`${session?.hidden_fees_label || 'Fees'}:`}
                    value={formatCurrency(order.hidden_fees, currency)}
                  />
                )}
                {isPositive(order.discount) && (
                  <Row label="Discount:" value={`-${formatCurrency(order.discount, currency)}`} cancelled />
                )}
                {parseFloat(order.tax || '0') > 0 && <Row label="Tax:" value={formatCurrency(order.tax, currency)} />}
                {parseFloat(order.tip || '0') > 0 && <Row label="Tip:" value={formatCurrency(order.tip, currency)} />}
                <hr className="totals-divider" />
                <Row label="Total:" value={formatCurrency(order.total_discounted_price, currency)} bold />
              </div>

              <div className="totals-group">
                {isPositive(order.past_balance) && (
                  <Row label="Past Payments:" value={formatCurrency(order.past_balance, currency)} />
                )}
                {isPositive(order.forward_balance) && (
                  <Row label="Carry Forward:" value={`-${formatCurrency(order.forward_balance, currency)}`} cancelled />
                )}
                <Row label="Amount Paid:" value={formatCurrency(order.amount_paid, currency)} />
                {isPositive(order.uncaptured_auth) && (
                  <Row label="Authorized:" value={formatCurrency(order.uncaptured_auth, currency)} />
                )}
                {isPositive(order.store_debit) && (
                  <Row label="Credited:" value={formatCurrency(order.store_debit, currency)} />
                )}
                {isPositive(order.amount_voided) && (
                  <Row label="Voided:" value={`-${formatCurrency(order.amount_voided, currency)}`} cancelled />
                )}
                {isPositive(order.settled_refund_amount) && (
                  <Row
                    label="Refunded:"
                    value={`-${formatCurrency(order.settled_refund_amount, currency)}`}
                    cancelled
                  />
                )}
                {isPositive(order.pending_refund_amount) && (
                  <Row
                    label={
                      <span className="inline-flex items-center gap-1">
                        Pending Refund:
                        <span
                          title={
                            'The refund is pending because the payment has not been captured yet.\n' +
                            'We will be able to process the refund once the charge is captured.\n' +
                            'This may take a few business days to appear.'
                          }>
                          <HelpCircle size={14} />
                        </span>
                      </span>
                    }
                    value={`-${formatCurrency(order.pending_refund_amount, currency)}`}
                    pending
                  />
                )}
                {isPositive(order.store_credit) && (
                  <Row label="Store Credit:" value={`-${formatCurrency(order.store_credit, currency)}`} cancelled />
                )}
                {isPositive(order.chargeback_amount) && (
                  <Row label="Chargeback:" value={`-${formatCurrency(order.chargeback_amount, currency)}`} cancelled />
                )}
                <hr className="totals-divider" />
                <Row label="Total Paid:" value={formatCurrency(order.total_remaining_balance, currency)} bold />
                {isPositive(order.amount_due) && (
                  <Row label="Amount Due:" value={formatCurrency(order.amount_due, currency)} bold />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default OrderTotalsInfo
